import type { Installation } from "@sitescene/types";

import { useEffect, useMemo, useRef, useState } from "react";

import { ApiError } from "@/api/client";
import { Button } from "@/components/ui/buttons/button";
import { Input } from "@/components/ui/input/input";
import { NativeSelect } from "@/components/ui/select/select-native";
import { TextArea } from "@/components/ui/textarea/textarea";
import { ConfigurationEditor } from "@/features/edge-configuration/ConfigurationEditor";
import { requestConfigurationNavigation } from "@/features/edge-configuration/navigation";
import {
	authorizeEdgeProvisioning,
	revokeEdgeProvisioning,
} from "@/features/installations/installation-api";
import { useInstallations } from "@/features/installations/use-installations";

const MAX_TIMEOUT_MILLISECONDS = 2_147_483_647;

interface OneTimeAuthorization {
	authorizationGeneration: number;
	expiresAt: string;
	token: string;
}

/** Operator surface for installation authorization lifecycle. */
export function EdgeProvisioningPage({ orgCode }: { orgCode: string }) {
	const mounted = useRef(false);
	const submitting = useRef(false);
	useEffect(() => {
		mounted.current = true;
		return () => {
			mounted.current = false;
		};
	}, []);
	const { data, error: loadError, isLoading, refetch } = useInstallations();
	const installations = useMemo(() => data ?? [], [data]);
	const [selectedId, setSelectedId] = useState("");
	const only = installations.length === 1 ? installations[0] : undefined;
	if (!selectedId && only) setSelectedId(only.id);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [error, setError] = useState<null | string>(null);
	const [oneTimeAuthorization, setOneTimeAuthorization] =
		useState<null | OneTimeAuthorization>(null);
	const [copyFeedbackVersion, setCopyFeedbackVersion] = useState(0);
	const [expiredOnPage, setExpiredOnPage] = useState(false);
	const [expiryFeedbackVersion, setExpiryFeedbackVersion] = useState(0);
	const [now, setNow] = useState(() => Date.now());
	const [isTokenExpanded, setIsTokenExpanded] = useState(false);

	const selected = useMemo(
		() => installations.find((item) => item.id === selectedId),
		[installations, selectedId],
	);
	const generation = selected?.provisioning?.authorizationGeneration ?? 0;
	const status = statusLabel(selected);
	const displayedTokenExpiresAt = oneTimeAuthorization?.expiresAt;
	const hasDisplayedToken = Boolean(oneTimeAuthorization);
	const authorizationExpired =
		expiredOnPage ||
		isProvisioningAuthorizationExpired(selected, now) ||
		isExpired(displayedTokenExpiresAt, now);
	const intent = status === "unauthorized" ? "authorize" : "reauthorize";
	const canAuthorize =
		Boolean(selected) &&
		status !== "completed" &&
		(status === "unauthorized" || status === "revoked" || authorizationExpired);
	const canRevoke =
		Boolean(selected) && status === "authorized" && !authorizationExpired;
	const authorizationDisabled = !canAuthorize || isSubmitting;
	const authorizationActionLabel =
		status === "unauthorized"
			? "Authorize installation"
			: authorizationExpired
				? "Issue new token"
				: "Reauthorize installation";

	useEffect(() => {
		if (status !== "authorized") return;
		const timer = window.setInterval(() => {
			if (!submitting.current) void refetch();
		}, 15000);
		return () => window.clearInterval(timer);
	}, [status, refetch]);

	useEffect(() => {
		const expiresAt =
			displayedTokenExpiresAt ?? selected?.provisioning?.authorizationExpiresAt;
		if ((!hasDisplayedToken && status !== "authorized") || !expiresAt) {
			queueMicrotask(() => setNow(Date.now()));
			return;
		}
		const expiresAtMillis = new Date(expiresAt).getTime();
		if (!Number.isFinite(expiresAtMillis)) return;

		const refreshExpiry = () => setNow(Date.now());
		const handleExpiry = () => {
			refreshExpiry();
			setOneTimeAuthorization(null);
			setCopyFeedbackVersion(0);
			setIsTokenExpanded(false);
			setExpiryFeedbackVersion((version) => version + 1);
		};
		queueMicrotask(refreshExpiry);
		const delay = Math.max(0, expiresAtMillis - Date.now()) + 1;
		if (delay <= 1) return;
		const timeout = window.setTimeout(
			handleExpiry,
			Math.min(delay, MAX_TIMEOUT_MILLISECONDS),
		);
		return () => window.clearTimeout(timeout);
	}, [
		displayedTokenExpiresAt,
		hasDisplayedToken,
		selected?.provisioning?.authorizationExpiresAt,
		status,
	]);

	// Deferred so the resets do not run synchronously in the effect body.
	useEffect(() => {
		if (!authorizationExpired) return;
		queueMicrotask(() => {
			setOneTimeAuthorization(null);
			setCopyFeedbackVersion(0);
			setIsTokenExpanded(false);
		});
	}, [authorizationExpired]);

	const selectInstallation = (id: string) =>
		requestConfigurationNavigation(() => {
			setSelectedId(id);
			setOneTimeAuthorization(null);
			setCopyFeedbackVersion(0);
			setExpiredOnPage(false);
			setExpiryFeedbackVersion(0);
			setIsTokenExpanded(false);
			setError(null);
		});

	const handleFailure = async (cause: unknown) => {
		if (!mounted.current) return;
		if (cause instanceof ApiError && cause.statusCode === 409) {
			await refetch();
			if (!mounted.current) return;
			setOneTimeAuthorization(null);
			setError(
				"Installation state changed before this request completed. Review the refreshed status before trying again.",
			);
			return;
		}
		setError(cause instanceof Error ? cause.message : "Request failed");
	};

	const authorize = async () => {
		if (!selected || authorizationDisabled || submitting.current) return;
		submitting.current = true;
		setIsSubmitting(true);
		setError(null);
		try {
			const result = await authorizeEdgeProvisioning(
				selected.external.id,
				{ expectedGeneration: generation, intent },
				orgCode,
			);
			if (!mounted.current) return;
			setOneTimeAuthorization(result);
			setCopyFeedbackVersion(0);
			setExpiredOnPage(false);
			setIsTokenExpanded(false);
			await refetch();
			if (!mounted.current) return;
		} catch (cause) {
			await handleFailure(cause);
		} finally {
			submitting.current = false;
			if (mounted.current) setIsSubmitting(false);
		}
	};

	const revoke = async () => {
		if (!selected || isSubmitting || submitting.current) return;
		if (isProvisioningAuthorizationExpired(selected)) {
			// Expiry invalidates the JWT but does not rewrite lifecycle state. Do
			// not silently turn an expired authorization into a staff revocation.
			setExpiredOnPage(true);
			setOneTimeAuthorization(null);
			setCopyFeedbackVersion(0);
			setIsTokenExpanded(false);
			setExpiryFeedbackVersion((version) => version + 1);
			setError(null);
			return;
		}
		if (!canRevoke) return;
		submitting.current = true;
		setIsSubmitting(true);
		setError(null);
		try {
			await revokeEdgeProvisioning(selected.external.id, generation, orgCode);
			if (!mounted.current) return;
			setOneTimeAuthorization(null);
			setCopyFeedbackVersion(0);
			setExpiredOnPage(false);
			setIsTokenExpanded(false);
			await refetch();
			if (!mounted.current) return;
		} catch (cause) {
			await handleFailure(cause);
		} finally {
			submitting.current = false;
			if (mounted.current) setIsSubmitting(false);
		}
	};

	const copyToken = async () => {
		if (!oneTimeAuthorization) return;
		setError(null);
		try {
			await navigator.clipboard.writeText(oneTimeAuthorization.token);
			if (!mounted.current) return;
			setCopyFeedbackVersion((version) => version + 1);
		} catch {
			if (!mounted.current) return;
			setCopyFeedbackVersion(0);
			setError(
				"The browser could not copy the JWT. Select and copy it directly from the field before leaving this page.",
			);
		}
	};

	return (
		<div className="mx-auto w-full max-w-7xl space-y-6">
			<header>
				<h1 className="text-2xl font-semibold text-primary">
					Lookout Installation
				</h1>
				<p className="mt-2 text-sm text-secondary">
					Manage Lookout installation authorization tokens for the selected
					installation.
				</p>
			</header>

			<section className="rounded-lg border border-secondary">
				<div className="border-b border-secondary p-4">
					<h2 className="font-semibold text-primary">Installation state</h2>
				</div>
				<div className="space-y-5 p-4">
					<NativeSelect
						disabled={isLoading || isSubmitting}
						label="Installation"
						onChange={(event) => selectInstallation(event.target.value)}
						options={[
							{ label: "Select an installation…", value: "" },
							...installations.map((installation) => ({
								label: installation.external.id,
								value: installation.id,
							})),
						]}
						value={selectedId}
					/>

					{loadError && (
						<p className="text-sm text-error-primary">{loadError.message}</p>
					)}

					{selected && (
						<div className="space-y-4 rounded-lg bg-primary p-4">
							<div className="flex flex-col gap-4 sm:flex-row sm:items-center">
								<div
									className={`flex flex-1 flex-wrap gap-8 rounded-lg p-3 ${expiryFeedbackVersion > 0 ? "attention-flash attention-flash-error" : ""}`}
									data-testid="authorization-status"
									key={expiryFeedbackVersion}
								>
									<div className="min-w-32">
										<p className="text-xs font-medium text-tertiary uppercase">
											Status
										</p>
										<p
											className={`mt-1 text-sm font-semibold ${
												authorizationExpired
													? "text-error-primary"
													: "text-primary"
											}`}
										>
											{authorizationExpired ? "Expired" : status}
										</p>
									</div>
									<div className="min-w-0 sm:flex-1">
										<p className="text-xs font-medium text-tertiary uppercase">
											Authorization expiry
										</p>
										<p className="mt-1 text-sm font-semibold text-primary">
											{selected.provisioning?.authorizationExpiresAt
												? new Date(
														selected.provisioning.authorizationExpiresAt,
													).toLocaleString(undefined, { timeZoneName: "short" })
												: "—"}
										</p>
									</div>
								</div>
								{canRevoke ? (
									<div className="sm:ml-auto">
										<Button
											color="secondary"
											isDisabled={isSubmitting}
											onClick={revoke}
											size="sm"
											type="button"
										>
											{isSubmitting ? "Revoking…" : "Revoke authorization"}
										</Button>
									</div>
								) : canAuthorize ? (
									<div className="sm:ml-auto">
										<Button
											isDisabled={authorizationDisabled}
											onClick={authorize}
											size="sm"
											type="button"
										>
											{isSubmitting ? "Requesting…" : authorizationActionLabel}
										</Button>
									</div>
								) : null}
							</div>

							{oneTimeAuthorization && (
								<div className="space-y-2 border-t border-secondary pt-4">
									<div className="flex flex-wrap items-center gap-2">
										<p className="text-sm font-semibold text-error-primary">
											Not retained — copy before leaving this page.
										</p>
										<span className="text-sm text-secondary">
											Expires{" "}
											{new Date(oneTimeAuthorization.expiresAt).toLocaleString(
												undefined,
												{ timeZoneName: "short" },
											)}
											.
										</span>
									</div>
									<div className="flex items-center gap-2">
										<Input
											aria-label="Installation JWT"
											className="min-w-0 flex-1"
											inputClassName="truncate font-mono text-xs"
											isReadOnly
											value={oneTimeAuthorization.token}
										/>
										<Button
											className="shrink-0"
											onClick={copyToken}
											size="sm"
											type="button"
										>
											Copy token
										</Button>
										<Button
											aria-expanded={isTokenExpanded}
											className="shrink-0"
											color="secondary"
											onClick={() => setIsTokenExpanded((value) => !value)}
											size="sm"
											type="button"
										>
											{isTokenExpanded ? "Hide token" : "Show token"}
										</Button>
									</div>
									{copyFeedbackVersion > 0 && (
										<p
											aria-live="polite"
											className="attention-flash attention-flash-success inline-block rounded px-2 py-1 text-sm text-success-primary"
											key={copyFeedbackVersion}
										>
											Token copied to clipboard.
										</p>
									)}
									{isTokenExpanded && (
										<TextArea
											aria-label="Full provisioning token"
											isReadOnly
											textAreaClassName="min-h-28 font-mono text-xs break-all"
											value={oneTimeAuthorization.token}
										/>
									)}
								</div>
							)}
						</div>
					)}

					{status === "completed" && (
						<p className="rounded-lg border border-secondary bg-primary p-4 text-sm text-secondary">
							Completion is terminal for this workflow. Device replacement
							requires a separate operator-authorized recovery process.
						</p>
					)}

					{error && <p className="text-sm text-error-primary">{error}</p>}
				</div>
			</section>
			{selected && (
				<ConfigurationEditor
					installation={selected.id}
					installationExternalId={selected.external.id}
					key={`${orgCode}/${selected.id}`}
					org={orgCode}
				/>
			)}
		</div>
	);
}

function isExpired(expiresAt: Date | string | undefined, now: number): boolean {
	if (!expiresAt) return false;
	const expiresAtMillis = new Date(expiresAt).getTime();
	return Number.isFinite(expiresAtMillis) && expiresAtMillis <= now;
}

function isProvisioningAuthorizationExpired(
	installation: Installation | undefined,
	now = Date.now(),
): boolean {
	const provisioning = installation?.provisioning;
	if (provisioning?.status !== "authorized") return false;
	return isExpired(provisioning.authorizationExpiresAt, now);
}

function statusLabel(installation: Installation | undefined): string {
	return installation?.provisioning?.status ?? "unauthorized";
}
